import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	WelcomeStep,
	WelcomeStepHandle,
	WelcomeStepStepsInfo
} from "./WelcomeStep/WelcomeStep";
import { WelcomeWizardStepKind } from "client/Api/Authentication/authentication";
import { LanguageStep } from "./LanguageStep";
import { PersonalInfoStep } from "./PersonalInfoStep";
import { ProfessionalInfoStep } from "./ProfessionalInfoStep";
import { saveFirstLoginDone, saveWelcomeScreenFormStep } from "client/Api/WelcomeScreen/welcomeScreen";
import {
	AlertManager,
	useAlerts,
	useStableImperativeCallback,
	useUnmountedCancellation
} from "client/Hooks";
import { BillingStep } from "./BillingStep";
import { BusyIndicator } from "client/Components";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { SharePointStep } from "./SharePointStep";
import { UserPreferencesStep } from "./UserPreferencesStep";
import { CompleteStep } from "./CompleteStep";
import { HttpRequestError } from "client/Api/httpClient";
import { PermissionChangeErrorMessage } from "./PermissionChangeErrorMessage";

export type WelcomeWizardStepsProps = {
	initialActiveStep: WelcomeWizardStepKind;
	isAdmin: boolean;
	onNext(): void;
};

export const WelcomeWizardSteps = (props: WelcomeWizardStepsProps): ReactElement | null => {
	const {
		isAdmin,
		initialActiveStep,
		onNext: onNextFromProps
	} = props;

	const steps = useMemo(
		(): readonly WelcomeWizardStep[] => generateSteps(
			isAdmin
		),
		[
			isAdmin
		]
	);

	const [activeStepIndex, setActiveStepIndex] = useState<number>(() => getStepIndex(initialActiveStep, steps));
	const [isBusy, setIsBusy] = useState<boolean>(false);

	const onNext: () => void = useStableImperativeCallback(onNextFromProps);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const stepRef: RefObject<WelcomeStepHandle | null> = useRef<WelcomeStepHandle>(null);

	const step: WelcomeWizardStep | undefined = steps[activeStepIndex];

	const stepsInfo = useMemo(
		(): WelcomeStepStepsInfo => ({
			active:  activeStepIndex,
			total: steps.length
		}),
		[
			activeStepIndex,
			steps
		]
	);

	const finalizeHandler = useCallback(
		async (): Promise<void> => {
			try {
				setIsBusy(true);
				await saveFirstLoginDone(globalCancellation);
				setIsBusy(false);
				onNext();
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			globalCancellation,
			onNext,
			alertManager
		]
	);

	const navigateHandler = useCallback(
		async (stepsCount: 1 | -1, shouldReloadThePage?: boolean): Promise<void> => {
			const nextStepIndex: number = activeStepIndex + stepsCount;

			if (nextStepIndex < 0) {
				return;
			}

			if (nextStepIndex >= steps.length) {
				finalizeHandler();
			} else {
				const nextStepKind: WelcomeWizardStepKind = steps[nextStepIndex]
					.kind;

				try {
					setIsBusy(true);
					await saveWelcomeScreenFormStep(
						{ formStep: nextStepKind },
						globalCancellation
					);

					if (shouldReloadThePage) {
						reloadCurrentPage();
						return;
					}

					setIsBusy(false);
					setActiveStepIndex(nextStepIndex);
					stepRef.current
						?.resetScroll();

				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					let message: string | ReactElement = localize("_GeneralErrorMessage");

					if (e instanceof HttpRequestError && e.statusCode === 403) {
						message = <PermissionChangeErrorMessage />;
					}

					alertManager.showError(message);
					setIsBusy(false);
				}
			}
		},
		[
			activeStepIndex,
			finalizeHandler,
			steps,
			globalCancellation,
			alertManager
		]
	);

	const nextHandler = useCallback(
		(shouldReloadThePage?: boolean): void => {
			navigateHandler(1, shouldReloadThePage);
		},
		[navigateHandler]
	);

	const prevHandler = useCallback(
		(): void => {
			navigateHandler(-1);
		},
		[navigateHandler]
	);

	useEffect(
		(): void => {
			if (!step) {
				finalizeHandler();
			}
		},
		[
			finalizeHandler,
			step
		]
	);

	if (!step) {
		return null;
	}

	const shouldRenderBackButton: boolean = stepsInfo.active !== 0;

	return (
		<WelcomeStep ref={stepRef}>
			{isBusy && <BusyIndicator/>}
			<WelcomeStep.BackButton
				onClick={prevHandler}
				isHidden={!shouldRenderBackButton}
			/>
			{step.renderer(nextHandler)}
			<WelcomeStep.StepIndicator stepsInfo={stepsInfo} />
		</WelcomeStep>
	);
};
WelcomeWizardSteps.displayName = "WelcomeWizardSteps";

type WelcomeWizardStep = {
	kind: WelcomeWizardStepKind;
	renderer(
		onNext: (shouldReloadThePage?: boolean) =>void
	): ReactElement;
};

function generateSteps(
	isAdmin: boolean,
): readonly WelcomeWizardStep[] {
	const steps: WelcomeWizardStep[] = [];
	const currentCulture: string = getCurrentCulture();

	steps.push({
		kind: WelcomeWizardStepKind.Culture,
		renderer: (onNext) => (
			<LanguageStep
				onNext={(culture) => {
					onNext(culture !== currentCulture);
				}}
			/>
		)
	});

	if (isAdmin) {
		steps.push({
			kind: WelcomeWizardStepKind.Billing,
			renderer: (onNext) => (
				<BillingStep
					onNext={onNext}
				/>
			)
		});

		steps.push({
			kind: WelcomeWizardStepKind.Documents,
			renderer: (onNext) => (
				<SharePointStep onNext={onNext} />
			)
		});
	}

	steps.push({
		kind: WelcomeWizardStepKind.PersonalInfo,
		renderer: (onNext) => (
			<PersonalInfoStep onNext={onNext} />
		)
	});

	steps.push({
		kind: WelcomeWizardStepKind.ProfessionalInfo,
		renderer: (onNext) => (
			<ProfessionalInfoStep onNext={onNext} />
		)
	});

	steps.push({
		kind: WelcomeWizardStepKind.UserPreferences,
		renderer: (onNext) => (
			<UserPreferencesStep onNext={onNext} />
		)
	});

	steps.push({
		kind: WelcomeWizardStepKind.AdditionalInfo,
		renderer: (onNext) => (
			<CompleteStep onNext={onNext} />
		)
	});

	return steps;
}

function getStepIndex(
	stepKind: WelcomeWizardStepKind,
	steps: readonly WelcomeWizardStep[]
): number {
	const currentStepIndex: number = steps.findIndex(x => x.kind === stepKind);

	if (currentStepIndex === -1) {
		return 0;
	}

	return currentStepIndex;
}